import { useEffect, useMemo, useState } from "react";

import { fetchTradePolicyFilterCatalog } from "../services/tradePolicyApi";
import type { TradePolicyCatalogField } from "../types/tradePolicy";

export type TradePolicyCatalogSnapshot = {
  catalogLoading: boolean;
  catalogError: string | null;
  catalogFields: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
};

export function useTradePolicyFilterCatalogFields(enabled = true): TradePolicyCatalogSnapshot {
  const [catalogLoading, setCatalogLoading] = useState(enabled);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogFields, setCatalogFields] = useState<TradePolicyCatalogField[]>([]);

  useEffect(() => {
    if (!enabled) {
      setCatalogLoading(false);
      setCatalogError(null);
      setCatalogFields([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const data = await fetchTradePolicyFilterCatalog();
        if (cancelled) return;
        setCatalogFields(Array.isArray(data.fields) ? data.fields : []);
      } catch (error) {
        if (cancelled) return;
        setCatalogError(error instanceof Error ? error.message : "Failed to load filter catalog.");
        setCatalogFields([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const fieldsByPath = useMemo(() => {
    const map = new Map<string, TradePolicyCatalogField>();
    for (const field of catalogFields) map.set(field.path, field);
    return map;
  }, [catalogFields]);

  return { catalogLoading, catalogError, catalogFields, fieldsByPath };
}
